import type { ReactElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";

/**
 * Rasterise an SVG string to a PNG data URI. The native bars load images with
 * UIImage(data:), which cannot decode SVG, so SVG icons are drawn to a canvas and
 * exported as PNG. The result is a template image (its alpha is tinted by the bar),
 * so the source colour does not matter.
 */
export function rasterizeSvg(svg: string, sizePt = 24, scale = 3): Promise<string | undefined> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (result: string | undefined) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = sizePt * scale;
        canvas.height = sizePt * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return settle(undefined);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        settle(canvas.toDataURL("image/png"));
      } catch {
        settle(undefined);
      }
    };
    image.onerror = () => settle(undefined);
    image.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
    // A malformed SVG can leave both events silent forever; bound the wait.
    setTimeout(() => settle(undefined), 1500);
  });
}

/**
 * Render a React icon element to static SVG markup and rasterise it to a PNG data
 * URI. renderToStaticMarkup is synchronous and runs anywhere (unlike flushSync,
 * which is a no-op inside a React effect). React omits the SVG xmlns and may omit a
 * pixel size, both of which a standalone data-URI SVG needs, so they are added.
 */
export function rasterizeReactIcon(element: ReactElement, sizePt = 24, scale = 3): Promise<string | undefined> {
  let markup: string;
  try {
    markup = renderToStaticMarkup(element);
  } catch {
    return Promise.resolve(undefined);
  }
  // The icon components wrap their <svg> in a <div>, so extract just the standalone
  // <svg>; a data-URI SVG must be the svg itself, not wrapped in HTML.
  const match = markup.match(/<svg[\s\S]*<\/svg>/);
  if (!match) return Promise.resolve(undefined);
  let svg = match[0];
  // Strips a mask="url(#id)" reference with no matching <mask id="id"> in the markup.
  svg = svg.replace(/\smask="url\(#([^)]+)\)"/g, (full, maskId) => {
    const escapedId = maskId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`<mask[^>]*\\bid="${escapedId}"`).test(svg) ? full : "";
  });
  // Only the root <svg> tag counts — a nested inner svg can already have xmlns/width.
  const rootTag = svg.match(/^<svg[^>]*>/)?.[0] ?? "";
  if (!/\bxmlns=/.test(rootTag)) {
    svg = svg.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
  }
  if (!/\bwidth=/.test(rootTag)) {
    svg = svg.replace("<svg", `<svg width="${sizePt}" height="${sizePt}"`);
  }
  return rasterizeSvg(svg, sizePt, scale);
}
